import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject, finalize, takeUntil } from 'rxjs';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { Role } from 'app/core/constants/roles.enum';
import { AppVersionService } from 'app/core/version/version.service';
import { VersionState } from 'app/core/version/version.types';
import { MailerService } from 'app/core/services';
import { Mailer } from 'app/core/interfaces';
import { FuseConfirmationService } from '@fuse/services/confirmation';

@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user'
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = false;
    user: User;
    versionState: VersionState;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    userService = inject(UserService);
    versionService = inject(AppVersionService);
    mailerService = inject(MailerService);
    confirmationService = inject(FuseConfirmationService);
    isVisitant = this.userService.isRole(Role.VISITOR);
    isSendingTestMail = false;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        this.versionService.versionState$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((versionState: VersionState) => {
                this.versionState = versionState;
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void
    {
        // Return if user is not available
        if ( !this.user )
        {
            return;
        }

        // Update the user
        // this._userService.update({
        //     ...this.user,
        //     status
        // }).subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }

    get canTestMail(): boolean
    {
        return this.user?.role === Role.ADMIN || this.user?.role === Role.OWNER;
    }

    sendTestMail(): void
    {
        const email = this.user?.email?.trim();

        if (!email)
        {
            this.openMailTestDialog(
                'Prueba de correo no disponible',
                'El usuario actual no tiene un email configurado para recibir la prueba.',
                'heroicons_outline:x-circle',
                'warn'
            );
            return;
        }

        if (this.isSendingTestMail)
        {
            return;
        }

        this.isSendingTestMail = true;
        this._changeDetectorRef.markForCheck();

        const displayName = this.user?.username || email;
        const testedAt = new Date().toLocaleString('es-ES');
        const mailBody: Mailer = {
            to: email,
            subject: '[LA CHOCO 2 - ERP] Prueba de configuracion de correo',
            text: `Hola ${displayName},\n\nEsta es una prueba de la configuracion de correo del ERP.\nFecha: ${testedAt}\n\nSi recibes este mensaje, el envio de correo esta funcionando correctamente.`,
            html: `
                <p>Hola <strong>${displayName}</strong>,</p>
                <p>Esta es una prueba de la configuracion de correo del ERP.</p>
                <p>Fecha de prueba: <strong>${testedAt}</strong></p>
                <p>Si recibes este mensaje, el envio de correo esta funcionando correctamente.</p>
            `
        };

        this.mailerService.sendMail(mailBody)
            .pipe(
                finalize(() => {
                    this.isSendingTestMail = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this.openMailTestDialog(
                        'Prueba de correo completada',
                        `Se ha enviado un correo de prueba a <strong>${email}</strong>.<br><br>Revisa la bandeja de entrada y la carpeta de enviados de Gmail para confirmar el resultado.`,
                        'heroicons_outline:mail',
                        'primary'
                    );
                },
                error: (error) => {
                    const detail = error?.error?.message || error?.message || 'No se pudo completar la prueba de correo.';

                    this.openMailTestDialog(
                        'Error en la prueba de correo',
                        `No se pudo enviar el correo de prueba a <strong>${email}</strong>.<br><br><strong>Detalle:</strong><br>${detail}`,
                        'heroicons_outline:x-circle',
                        'warn'
                    );
                }
            });
    }

    private openMailTestDialog(title: string, message: string, iconName: string, iconColor: 'primary' | 'accent' | 'warn' | 'basic' | 'info' | 'success' | 'warning'): void
    {
        this.confirmationService.open({
            title,
            message,
            icon: {
                show: true,
                name: iconName,
                color: iconColor
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Cerrar',
                    color: 'primary'
                },
                cancel: {
                    show: false,
                    label: 'Cancelar'
                }
            },
            dismissible: false
        });
    }
}
